// Form controls: one Field wires a label, a hint and an error to its control (for/id and
// aria-describedby), and the text input, text area, select, checkbox, switch and segmented
// control are the only form elements a screen ever shows.
import "./config";
import { useId } from "react";
import { Input, Text, TextArea as TextAreaBase, View, XStack, styled } from "tamagui";
import { pressed } from "./aria";
import { Pressable } from "./controls";
import { Icon, type IconName } from "./Icon";
import { Column, Fill, Row } from "./layout";
import { Body, Caption, Eyebrow } from "./text";

/** What a control needs to be described by its Field. */
export interface FieldAria {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export interface FieldProps {
  label: string;
  hint?: string | undefined;
  /** The error text; it is announced when it appears and marks the control invalid. */
  error?: string | undefined;
  /** Keeps the label for assistive technology but does not draw it. */
  hideLabel?: boolean;
  /** Use an id the caller already refers to (a test, an aria-controls). */
  id?: string | undefined;
  children: (aria: FieldAria) => React.ReactNode;
}

const FieldLabel = styled(Eyebrow, {
  name: "FieldLabel",
  render: "label",
  fontSize: "1",
  color: "textDim",
});

/** A labelled control with an optional hint and error, all tied together for assistive technology. */
export function Field({
  label,
  hint,
  error,
  hideLabel = false,
  id,
  children,
}: FieldProps): React.JSX.Element {
  const generated = useId();
  const controlId = id ?? generated;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const described = [
    ...(hint === undefined ? [] : [hintId]),
    ...(error === undefined ? [] : [errorId]),
  ].join(" ");
  return (
    <Column gap="2" flex={1}>
      <FieldLabel
        htmlFor={controlId}
        // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
        {...(hideLabel
          ? {
              position: "absolute" as const,
              width: 1,
              height: 1,
              overflow: "hidden" as const,
              clipPath: "inset(50%)",
            }
          : {})}
      >
        {label}
      </FieldLabel>
      {children({
        id: controlId,
        ...(described === "" ? {} : { "aria-describedby": described }),
        ...(error === undefined ? {} : { "aria-invalid": true }),
      })}
      {hint === undefined ? null : (
        <Caption id={hintId} lineHeight={4}>
          {hint}
        </Caption>
      )}
      {error === undefined ? null : (
        <Body id={errorId} size="small" weight="medium" tone="error" role="alert">
          {error}
        </Body>
      )}
    </Column>
  );
}

const fieldLook = {
  width: "100%",
  minHeight: "var(--uiTarget)",
  paddingHorizontal: "3",
  paddingVertical: "3",
  borderRadius: "2",
  borderWidth: "var(--borderW)",
  borderColor: { default: "lineInput", focus: "accent" },
  backgroundColor: "surface",
  color: "text",
  fontFamily: "body",
  fontSize: "4",
  placeholderTextColor: "textFaint",
  outlineWidth: { default: 0, focus: 3 },
  outlineStyle: "solid",
  outlineColor: { default: "accentSoft", a11yLight: "text", a11yDark: "text" },
} as const;

const InputFrame = styled(Input, {
  name: "TextInput",
  ...fieldLook,
  variants: { invalid: { true: { borderColor: "error" } } } as const,
});
const AreaFrame = styled(TextAreaBase, {
  name: "TextArea",
  ...fieldLook,
  minHeight: 96,
  variants: { invalid: { true: { borderColor: "error" } } } as const,
});

type InputProps = Omit<React.ComponentProps<typeof InputFrame>, "invalid" | "id">;
type AreaProps = Omit<React.ComponentProps<typeof AreaFrame>, "invalid" | "id">;

interface FieldCopy {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  hideLabel?: boolean;
  id?: string | undefined;
}

export type TextInputProps = InputProps & FieldCopy;

/** A single-line text field with its label, hint and error. */
export function TextInput({
  label,
  hint,
  error,
  hideLabel,
  id,
  ...rest
}: TextInputProps): React.JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} id={id} hideLabel={hideLabel === true}>
      {/* nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- typed props */}
      {(aria) => <InputFrame invalid={error !== undefined} {...aria} {...rest} />}
    </Field>
  );
}

export type TextAreaProps = AreaProps & FieldCopy;

/** A multi-line text field with its label, hint and error. */
export function TextArea({
  label,
  hint,
  error,
  hideLabel,
  id,
  ...rest
}: TextAreaProps): React.JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} id={id} hideLabel={hideLabel === true}>
      {/* nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- typed props */}
      {(aria) => <AreaFrame invalid={error !== undefined} {...aria} {...rest} />}
    </Field>
  );
}

const SelectFrame = styled(View, {
  name: "SelectField",
  render: "select",
  ...fieldLook,
  paddingVertical: 0,
  variants: { invalid: { true: { borderColor: "error" } } } as const,
});

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps extends FieldCopy {
  value: string;
  options: readonly SelectOption[];
  onValueChange: (value: string) => void;
  disabled?: boolean;
  name?: string;
  testId?: string;
}

/** A native select: the platform's own picker is the best one on a phone. */
export function SelectField({
  label,
  hint,
  error,
  hideLabel,
  id,
  value,
  options,
  onValueChange,
  disabled = false,
  name,
  testId,
}: SelectFieldProps): React.JSX.Element {
  // Tamagui's props do not list `value`; the DOM select needs it to stay controlled.
  const nativeSelect = { value };
  return (
    <Field label={label} hint={hint} error={error} id={id} hideLabel={hideLabel === true}>
      {(aria) => (
        <SelectFrame
          invalid={error !== undefined}
          // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
          {...nativeSelect}
          disabled={disabled}
          // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
          {...(name === undefined ? {} : { name })}
          // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
          {...(testId === undefined ? {} : { "data-testid": testId })}
          onChange={(event) => {
            if (event.target instanceof HTMLSelectElement) onValueChange(event.target.value);
          }}
          // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
          {...aria}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectFrame>
      )}
    </Field>
  );
}

const ChoiceRow = styled(Pressable, {
  name: "ChoiceRow",
  flexDirection: "row",
  justifyContent: "flex-start",
  gap: "3",
  width: "100%",
  minHeight: "var(--uiTarget)",
  paddingHorizontal: "3",
  paddingVertical: "3",
  borderRadius: "2",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: "surface",
  variants: {
    on: { true: { borderColor: "accentLine", backgroundColor: "accentSoft" } },
  } as const,
});

interface ToggleProps {
  label: string;
  /** A second line under the label. */
  hint?: string | undefined;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  testId?: string;
}

function ToggleText({
  id,
  hintId,
  label,
  hint,
}: {
  id: string;
  hintId: string;
  label: string;
  hint?: string | undefined;
}) {
  return (
    <Fill>
      <Body id={id} size="small" weight="medium">
        {label}
      </Body>
      {hint === undefined ? null : <Caption id={hintId}>{hint}</Caption>}
    </Fill>
  );
}

export type CheckboxProps = ToggleProps;

/** One yes/no choice drawn as a whole tappable row; Space or Enter toggles it from the keyboard. */
export function Checkbox({
  label,
  hint,
  checked,
  onCheckedChange,
  disabled = false,
  testId,
}: CheckboxProps): React.JSX.Element {
  const labelId = useId();
  const hintId = useId();
  return (
    <ChoiceRow
      role="checkbox"
      aria-checked={checked}
      aria-labelledby={labelId}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(hint === undefined ? {} : { "aria-describedby": hintId })}
      on={checked}
      disabled={disabled}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(testId === undefined ? {} : { "data-testid": testId })}
      onPress={() => {
        onCheckedChange(!checked);
      }}
    >
      <View
        width={22}
        height={22}
        flexShrink={0}
        borderRadius="1"
        borderWidth="var(--borderW)"
        borderColor={checked ? "accent" : "lineInput"}
        backgroundColor={checked ? "accent" : "transparent"}
        alignItems="center"
        justifyContent="center"
        aria-hidden
      >
        {checked ? <Icon name="check" size={16} tone="accentInk" strokeWidth={2.4} /> : null}
      </View>
      <ToggleText id={labelId} hintId={hintId} label={label} hint={hint} />
    </ChoiceRow>
  );
}

export type SwitchProps = ToggleProps;

/** An on/off setting that takes effect at once: a labelled track with a thumb. */
export function Switch({
  label,
  hint,
  checked,
  onCheckedChange,
  disabled = false,
  testId,
}: SwitchProps): React.JSX.Element {
  const labelId = useId();
  const hintId = useId();
  return (
    <Pressable
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(hint === undefined ? {} : { "aria-describedby": hintId })}
      flexDirection="row"
      justifyContent="flex-start"
      gap="3"
      width="100%"
      minHeight="var(--uiTarget)"
      disabled={disabled}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(testId === undefined ? {} : { "data-testid": testId })}
      onPress={() => {
        onCheckedChange(!checked);
      }}
    >
      <ToggleText id={labelId} hintId={hintId} label={label} hint={hint} />
      <XStack
        width={46}
        height={28}
        flexShrink={0}
        borderRadius="pill"
        padding={3}
        alignItems="center"
        borderWidth="var(--borderW)"
        borderColor={checked ? "accent" : "lineInput"}
        backgroundColor={checked ? "accent" : "surface3"}
        aria-hidden
      >
        <View
          width={20}
          height={20}
          borderRadius="pill"
          backgroundColor={checked ? "accentInk" : "textDim"}
          x={checked ? 18 : 0}
          transitionProperty="transform"
          transitionDuration="var(--durFast)"
        />
      </XStack>
    </Pressable>
  );
}

const SegmentedFrame = styled(XStack, {
  name: "Segmented",
  gap: "1",
  padding: "1",
  borderRadius: "2",
  backgroundColor: "surface2",
  borderWidth: "var(--borderW)",
  borderColor: "line",
});

const Segment = styled(Pressable, {
  name: "Segment",
  flex: 1,
  flexDirection: "row",
  gap: "2",
  minHeight: 40,
  paddingHorizontal: "3",
  borderRadius: 9,
  borderWidth: 1,
  borderColor: "transparent",
  variants: {
    active: {
      true: {
        backgroundColor: { default: "surface", a11yLight: "text", a11yDark: "text" },
        borderColor: "lineStrong",
      },
    },
  } as const,
});

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
  testId?: string;
}

export interface SegmentedProps<T extends string> {
  /** The group's accessible name. */
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
}

/** Two to four exclusive choices side by side, each a toggle button inside a labelled group. */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onValueChange,
}: SegmentedProps<T>): React.JSX.Element {
  return (
    <SegmentedFrame role="group" aria-label={label}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Segment
            key={option.value}
            active={active}
            // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
            {...pressed(active)}
            // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
            {...(option.testId === undefined ? {} : { "data-testid": option.testId })}
            onPress={() => {
              onValueChange(option.value);
            }}
          >
            {option.icon === undefined ? null : (
              <Icon name={option.icon} size={14} tone={active ? undefined : "textDim"} />
            )}
            <Text
              fontFamily="body"
              fontSize="3"
              fontWeight="700"
              color={active ? { default: "text", a11yLight: "bg", a11yDark: "bg" } : "textDim"}
            >
              {option.label}
            </Text>
          </Segment>
        );
      })}
    </SegmentedFrame>
  );
}

/** A row of fields that share the width (two halves of a date, a range). */
export const FieldRow = styled(Row, { name: "FieldRow", alignItems: "flex-start", gap: "3" });
